/**
 * Unified Quiz Modal Component
 * Handles lesson, level, and course quizzes
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import {
  X,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  AlertTriangle,
  Play,
  ClipboardList,
  Trophy,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import {
  contentService,
  LessonQuizDto,
  LevelQuizDto,
  CourseQuizDto,
  LessonQuizSubmissionDto,
  LevelQuizSubmissionDto,
  CourseQuizSubmissionDto,
  EnhancedQuizResultDto,
  QuestionType,
  QuestionResultDto,
} from '../../services/contentService';
import { notifyGamificationAfterAction, readXpAwarded } from '../../utils/gamificationNotif';
import { isQuizResultPassed } from '../../utils/patchLessonProgress';
import { useNotifications } from '../feedback/NotificationSystem';
import { AudioPlayer } from '../shared/AudioPlayer';
import { FarsiKeyboardInput } from '../shared/FarsiKeyboardInput';
import { userActionLogger } from '../../services/userActionLogger';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useLocalizedContent } from '../../hooks/useLocalizedContent';
import { useContentLocaleScope } from '../../hooks/useContentLocaleScope';
import { isDesignedForNonFarsiUsers } from '../../utils/nonFarsiContentHint';
import { EnglishLockedContentHintIcon } from '../shared/EnglishLockedContentHintIcon';
import { localizeQuizExerciseQuestion, pickQuestionExplanation } from '../../utils/localizedContent';
import { extractFormattedTextDirection } from '../../utils/formattedTextDirection';
import { RichContentBlocksView } from './RichContentBlocksView';
import { localizeQuizNavigationGuidance } from '../../utils/quizNavigationGuidance';
import { QuizResultView } from './QuizResultView';
import { localeMirroredNavArrowClass } from '../../utils/localeNavArrow';
import { FormattedTextBlock } from './FormattedTextBlock';
import { PersianText } from '../shared/PersianText';
import { FontSizeProvider } from './FontSizeContext';
import { htmlContainsTerm } from '../../utils/searchHighlight';
import { isQuizAnsweredForProgress } from '../../utils/quizAnswerProgress';
import { isCommentQuestion, isPageSeparatorQuestion } from '../../utils/quizQuestionCount';
import { normalizeAppHostedUploadUrl } from '../../utils/normalizeAppHostedUploadUrl';
import {
  buildFindTheWordsHint,
  getCrosswordEntries,
  getFindTheWordsWords,
  initialCrosswordAnswer,
  initialOrderFromOptions,
  parseFoundIds,
  resolveFindTheWordsGrid,
} from '../../utils/interactiveQuestionHelpers';
import { LearnerSlideModalShell, LearnerSlideModalPanel } from '../shared/LearnerSlideModalShell';
import { LearnerAlertDialog } from '../shared/LearnerAlertDialog';
import { useLearnerSlideModalOnMount } from '../../hooks/useLearnerSlideModal';
import { useLearnerSlidePhase } from '../../hooks/useLearnerSlidePhase';
import { PuzzleTouchDragGhost } from './PuzzleTouchDragGhost';
import { SortParagraphsPlayer } from './questionTypes/SortParagraphsPlayer';
import { EssayAnswerInput } from './questionTypes/EssayAnswerInput';
import { FindTheWordsPlayer, serializeFoundIds } from './questionTypes/FindTheWordsPlayer';
import { CrosswordPlayer } from './questionTypes/CrosswordPlayer';

type QuizContentPhase = 'browse' | 'intro' | 'questions' | 'results';

const QUIZ_MODAL_PANEL_CLASS =
  'learner-run-sheet max-h-[min(90vh,820px)] w-full border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900';

const SEARCH_HIGHLIGHT_DURATION_MS = 5000;

function portalQuizModal(node: React.ReactNode): React.ReactNode {
  return typeof document !== 'undefined' ? createPortal(node, document.body) : node;
}

export type QuizType = 'lesson' | 'level' | 'course';

interface QuizModalProps {
  quizType: QuizType;
  quizId: number;
  previousScore?: number;
  onComplete?: (result: EnhancedQuizResultDto) => void;
  onClose: () => void;
  /** When set, result-screen Continue calls this instead of onComplete + onClose (sequential quiz run). */
  onFlowStageComplete?: (result: EnhancedQuizResultDto) => void;
  /** Placement flow: allow quiz-to-quiz navigation inside modal. */
  placementNavigation?: {
    canPrev: boolean;
    canNext: boolean;
    onPrev: () => void;
    onNext: () => void;
  };
  /** Placement flow: learner marks current quiz as too easy without taking it. */
  onPlacementSkipEasy?: () => void;
  /** Placement flow: do not enforce countdown timer. */
  disableTimeLimit?: boolean;
  /** Placement flow: skip the catalog-style intro and open questions directly. */
  placementDirectStart?: boolean;
  /** From global search: show intro copy only; timer starts after the learner clicks Start. */
  browseMode?: boolean;
  /** Optional passing score from placement flow item (used when learner quiz payload is normalized). */
  placementPassingScoreOverride?: number;
  /** Placement flow: learner accepts current quiz as fitted level. */
  onPlacementConsiderCurrent?: () => void;
  /**
   * When set (typically from the global learner-search opening the modal in
   * `browseMode`), every question whose stripped body or option text contains
   * this substring is flashed with the search-result pulse and the term is
   * wrapped with `<mark>` inside its FormattedTextBlock.
   */
  searchHighlightTerm?: string;
  /** Fired once when this search flash starts, so a later visit does not play it again. */
  onSearchHighlightConsumed?: () => void;
  /** When true, quiz title/description and question bodies use English (page chrome stays in UI locale). */
  contentForceEnglish?: boolean;
  /** Opens the English-primary content hint (shown as a corner icon in fa UI). */
  onEnglishLockedHintClick?: () => void;
}

type UnifiedQuizDto = LessonQuizDto | LevelQuizDto | CourseQuizDto;

/**
 * Safely parse quiz options that may be in JSON format or comma-separated string
 */
const parseQuizOptions = (options: string | null | undefined): string[] => {
  if (!options) return [];
  
  // Try to parse as JSON first
  try {
    const parsed = JSON.parse(options);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    // If JSON parsing fails, treat as comma-separated string
  }
  
  // Split by comma and trim each option
  return options.split(',').map(opt => opt.trim()).filter(opt => opt.length > 0);
};

/**
 * Extract text direction (RTL/LTR) from formatting or HTML content
 */
const extractTextDirection = extractFormattedTextDirection;

/** Learner quiz questions may expose `id` or `Id`; coerce for submit payload (NaN → skip). */
function coerceQuizQuestionId(q: any): number | null {
  const v = q?.id ?? q?.Id;
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseInt(String(v).trim(), 10);
  return Number.isFinite(n) ? n : null;
}

function shuffleArrayInPlace<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i] as T;
    arr[i] = arr[j] as T;
    arr[j] = tmp;
  }
}

/**
 * Shuffle only within segments separated by page separators and comment rows.
 * Comments and page breaks keep their authored order; only real questions shuffle within each segment.
 */
function orderQuestionsForSession<T extends { question?: string; textContent?: string }>(
  questions: T[],
  shuffle: boolean
): T[] {
  if (!shuffle || questions.length === 0) return [...questions];
  const out: T[] = [];
  let chunk: T[] = [];
  const flush = () => {
    if (chunk.length) {
      shuffleArrayInPlace(chunk);
      out.push(...chunk);
      chunk = [];
    }
  };
  for (const q of questions) {
    if (isPageSeparatorQuestion(q) || isCommentQuestion(q)) {
      flush();
      out.push(q);
    } else {
      chunk.push(q);
    }
  }
  flush();
  return out;
}

function buildMcOptionShuffleMap(questions: any[]): Record<number, any[]> {
  const map: Record<number, any[]> = {};
  for (const q of questions) {
    if (!q?.id || !q?.questionOptions?.length) continue;
    const flag = Boolean(q.shuffleMultipleChoiceOptions ?? q.ShuffleMultipleChoiceOptions);
    if (!flag) continue;
    const opts = [...q.questionOptions].sort(
      (a: { displayOrder?: number }, b: { displayOrder?: number }) =>
        (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    );
    shuffleArrayInPlace(opts);
    map[q.id] = opts;
  }
  return map;
}

export const QuizModal: React.FC<QuizModalProps> = ({
  quizType,
  quizId,
  previousScore,
  onComplete,
  onClose,
  onFlowStageComplete,
  placementNavigation,
  onPlacementSkipEasy,
  disableTimeLimit = false,
  placementDirectStart = false,
  browseMode = false,
  placementPassingScoreOverride,
  onPlacementConsiderCurrent,
  searchHighlightTerm,
  onSearchHighlightConsumed,
  contentForceEnglish = false,
  onEnglishLockedHintClick,
}) => {
  const { showError } = useNotifications();
  const { t } = useTranslation(['learning', 'common']);
  const { direction } = useAppLocale();
  const [currentQuiz, setCurrentQuiz] = useState<UnifiedQuizDto | null>(null);
  const quizEnglishLocked =
    contentForceEnglish || (currentQuiz != null && isDesignedForNonFarsiUsers(currentQuiz));
  const { pickTitle, pickDescription, locale } = useLocalizedContent({
    forceEnglish: quizEnglishLocked,
  });
  const { tContent } = useContentLocaleScope(quizEnglishLocked, ['learning', 'common']);
  const contentIsRtl = direction === 'rtl';
  const navArrowClass = (size: string) => localeMirroredNavArrowClass(contentIsRtl, size);
  /** Modal chrome, intro, timer, and results follow UI locale. */
  const tc = t;
  /** Question-phase placeholders when content is English-locked. */
  const tcQ = quizEnglishLocked ? tContent : t;
  const formattedTextDir = quizEnglishLocked ? ('ltr' as const) : undefined;
  const questionContentLocale = quizEnglishLocked ? ('en' as const) : locale;
  const { user } = useAuth();
  /** After the learner starts (or placement direct-start), ordered question list for this attempt; null before start. */
  const [sessionQuestions, setSessionQuestions] = useState<any[] | null>(null);
  const [sessionMcOptionsByQuestionId, setSessionMcOptionsByQuestionId] = useState<Record<number, any[]>>({});
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [puzzleOrders, setPuzzleOrders] = useState<Record<number, number[]>>({}); // Store puzzle word order by question ID
  const [puzzleDragging, setPuzzleDragging] = useState<Record<number, number | null>>({}); // Track dragged optionId per question (for highlight by card, not position)
  const [puzzleDragPreviewIndex, setPuzzleDragPreviewIndex] = useState<{ questionId: number; index: number } | null>(null); // Preview drop index during drag (apply only on drop)
  // Floating "ghost" that follows the finger during a touch drag. Lets the
  // dragged card visually stick to the touch even when no preview swap has
  // fired yet (the previous behavior was that the card sat still until the
  // finger crossed into another card).
  const [puzzleTouchGhost, setPuzzleTouchGhost] = useState<{
    questionId: number;
    optionId: number;
    clientX: number;
    clientY: number;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
  } | null>(null);
  const puzzleDraggedIdRef = useRef<number | null>(null);
  const puzzleDragOverIndexRef = useRef<{ questionId: number; index: number } | null>(null);
  const puzzleTouchQuestionIdRef = useRef<number | null>(null);
  const puzzleTouchOrderRef = useRef<number[]>([]);
  const puzzleDocTouchCleanupRef = useRef<(() => void) | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  /** Total countdown seconds for the active attempt (null when there's no time limit). */
  const [totalQuizSeconds, setTotalQuizSeconds] = useState<number | null>(null);
  /** Brief 5s flashing badge state shown when crossing the 70% / 90% elapsed thresholds. */
  const [timerHint, setTimerHint] = useState<'warning' | 'critical' | null>(null);
  const warningHintShownRef = useRef(false);
  const criticalHintShownRef = useRef(false);
  const timerHintClearTimeoutRef = useRef<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState<EnhancedQuizResultDto | null>(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const quizResultRef = useRef<EnhancedQuizResultDto | null>(null);
  const parentNotifiedRef = useRef(false);
  const effectivePassingScore = placementPassingScoreOverride ?? currentQuiz?.passingScore ?? 70;

  useEffect(() => {
    quizResultRef.current = quizResult;
  }, [quizResult]);

  useEffect(() => {
    parentNotifiedRef.current = false;
    quizResultRef.current = null;
  }, [quizId]);

  const notifyParentIfPassed = useCallback(
    (result: EnhancedQuizResultDto) => {
      if (!onComplete || !isQuizResultPassed(result) || parentNotifiedRef.current) return;
      parentNotifiedRef.current = true;
      onComplete(result);
    },
    [onComplete]
  );

  const finishQuizClose = useCallback(() => {
    const result = quizResultRef.current;
    if (result) notifyParentIfPassed(result);
    onClose();
  }, [notifyParentIfPassed, onClose]);

  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [partialSubmitCount, setPartialSubmitCount] = useState<number | null>(null);

  const beforeQuizClose = useCallback(() => {
    if (showExitConfirm) {
      setShowExitConfirm(false);
      return true;
    }
    if (partialSubmitCount != null) {
      setPartialSubmitCount(null);
      return true;
    }
    if (quizStarted && !quizResult) {
      setShowExitConfirm(true);
      return true;
    }
    return false;
  }, [showExitConfirm, partialSubmitCount, quizStarted, quizResult]);

  const {
    mounted: slideMounted,
    visible: slideVisible,
    panelRef: slidePanelRef,
    requestClose: requestQuizClose,
    forceClose: forceQuizClose,
    handleExitTransitionEnd,
  } = useLearnerSlideModalOnMount(finishQuizClose, beforeQuizClose, {
    historyBackId: 'quiz-modal',
  });

  const handleDismiss = requestQuizClose;
  const isPlacementFlow = Boolean(onFlowStageComplete && placementNavigation);
  /** Skip catalog intro and stay on the questions panel (including the loading shell). */
  const skipIntroPhase = placementDirectStart && !browseMode;
  /** After search browse preview, learner chose "Start quiz" → show normal pre-participation intro next. */
  const [browsePreviewConsumed, setBrowsePreviewConsumed] = useState(false);
  const [loadedPreviousScore, setLoadedPreviousScore] = useState<number | undefined>(undefined);
  const [trophyAnimationError, setTrophyAnimationError] = useState(false);
  const trophyAnimationContainerRef = useRef<HTMLDivElement>(null);
  const [errorAnimationError, setErrorAnimationError] = useState(false);
  const errorAnimationContainerRef = useRef<HTMLDivElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  /** Wall-clock start when the learner clicks Start (used for profile quiz time analytics). */
  const quizSessionStartMsRef = useRef<number | null>(null);
  const placementFailHandledRef = useRef(false);
  /**
   * Map of `questionId → ref` used by the global-search auto-scroll/flash.
   * Populated lazily during render and read inside the post-load effect.
   */
  const questionRefsRef = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const [pulsingQuestionIds, setPulsingQuestionIds] = useState<Set<number>>(() => new Set());
  const highlightTimerRef = useRef<number | null>(null);

  const activeQuestions = useMemo(() => {
    const raw =
      quizStarted && sessionQuestions !== null
        ? sessionQuestions
        : ((currentQuiz?.questions as any[]) ?? []);
    return raw.map((q) => localizeQuizExerciseQuestion(q, questionContentLocale));
  }, [quizStarted, sessionQuestions, currentQuiz?.questions, questionContentLocale]);

  /** Match server result rows to the order the learner saw (shuffle / pages). */
  const questionResultsInAttemptOrder = useMemo(() => {
    if (!quizResult?.questionResults?.length) return [];
    const orderedQs = sessionQuestions ?? (currentQuiz?.questions as any[]) ?? [];
    const answerable = orderedQs.filter((q: any) => !isPageSeparatorQuestion(q) && !isCommentQuestion(q));
    const order = new Map(answerable.map((q: any, i: number) => [q.id, i]));
    return [...quizResult.questionResults].sort(
      (a, b) => (order.get(a.questionId) ?? 99999) - (order.get(b.questionId) ?? 99999)
    );
  }, [quizResult, sessionQuestions, currentQuiz?.questions]);

  const localizedGuidanceMessage = useMemo(() => {
    if (!quizResult?.navigationGuidance) return '';
    return localizeQuizNavigationGuidance(
      tc,
      quizType,
      quizResult.passed,
      quizResult.navigationGuidance
    );
  }, [quizResult, quizType, tc]);

  useEffect(() => {
    loadQuiz();
  }, [quizType, quizId]);

  useEffect(() => {
    setBrowsePreviewConsumed(false);
  }, [quizType, quizId]);

  // When quiz result is shown, scroll the content area to top so the result message is visible first
  useEffect(() => {
    if (quizResult && contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, [quizResult]);

  /** Timer expiry or successful submit — close any open confirm dialogs before results show. */
  useEffect(() => {
    if (!quizResult) return;
    setShowExitConfirm(false);
    setPartialSubmitCount(null);
  }, [quizResult]);

  useEffect(() => {
    if (browsePreviewConsumed && contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, [browsePreviewConsumed]);

  // Placement flow: return to wizard decision box immediately on result (pass/fail).
  useEffect(() => {
    if (!isPlacementFlow || !quizResult || placementFailHandledRef.current) return;
    placementFailHandledRef.current = true;
    onFlowStageComplete?.(quizResult);
  }, [isPlacementFlow, onFlowStageComplete, quizResult]);

  const handleSubmit = useCallback(async (options?: { autoSubmitOnTimeout?: boolean; skipPartialConfirm?: boolean }) => {
    if (!currentQuiz) return;
    const autoSubmitOnTimeout = Boolean(options?.autoSubmitOnTimeout);
    const skipPartialConfirm = Boolean(options?.skipPartialConfirm);

    if (autoSubmitOnTimeout) {
      setShowExitConfirm(false);
      setPartialSubmitCount(null);
    }

    const allowPartial = Boolean(
      (currentQuiz as any)?.allowUnansweredQuestionsOnSubmit ??
        (currentQuiz as any)?.AllowUnansweredQuestionsOnSubmit
    );
    const qs = (currentQuiz.questions as any[]) ?? [];
    const answerableCatalog = qs.filter(
      (q: any) => !isPageSeparatorQuestion(q) && !isCommentQuestion(q)
    );
    const orderedSource = (sessionQuestions ?? qs).filter(
      (q: any) => !isPageSeparatorQuestion(q) && !isCommentQuestion(q)
    );
    const answerableForSubmit = orderedSource;
    const unanswered = answerableForSubmit.filter((q: any) => {
      const qid = coerceQuizQuestionId(q);
      if (qid == null) return true;
      return !isQuizAnsweredForProgress(q, answers[qid]);
    });
    // When the countdown reaches zero, auto-submit whatever the learner has so
    // far (for quizzes that allow unanswered questions). The confirmation
    // prompt is only relevant when the learner clicks Submit themselves.
    if (allowPartial && unanswered.length > 0 && !autoSubmitOnTimeout && !skipPartialConfirm) {
      setPartialSubmitCount(unanswered.length);
      return;
    }

    try {
      setSubmitting(true);
      let result: EnhancedQuizResultDto;

      const elapsedSecs =
        quizSessionStartMsRef.current != null
          ? Math.max(0, Math.floor((Date.now() - quizSessionStartMsRef.current) / 1000))
          : 0;
      const timePayload = {
        ...(elapsedSecs > 0 ? { totalTimeSpentSeconds: elapsedSecs } : {}),
        ...(autoSubmitOnTimeout ? { submittedOnTimeExpiry: true } : {}),
      };

      const seenSubmitIds = new Set<number>();
      const orderedAnswers: { questionId: number; answer: string }[] = [];
      const pushSubmitRow = (q: any) => {
        const qid = coerceQuizQuestionId(q);
        if (qid == null || seenSubmitIds.has(qid)) return;
        seenSubmitIds.add(qid);
        let text = answers[qid] === undefined || answers[qid] === null ? '' : String(answers[qid]);
        if (q.questionType === QuestionType.Crossword) {
          const blank = initialCrosswordAnswer(q);
          if (blank) {
            const chars = text.split('');
            text = blank
              .split('')
              .map((ch: string, i: number) => {
                if (ch === '#') return '#';
                const u = chars[i];
                if (u == null || u === '#' || u === '' || u === ' ') return '.';
                return u;
              })
              .join('');
          }
        }
        orderedAnswers.push({ questionId: qid, answer: text });
      };
      for (const q of orderedSource) pushSubmitRow(q);
      for (const q of answerableCatalog) pushSubmitRow(q);

      switch (quizType) {
        case 'lesson':
          const lessonSubmission: LessonQuizSubmissionDto = {
            lessonQuizId: currentQuiz.id,
            answers: orderedAnswers,
            ...timePayload
          };
          result = await contentService.submitLessonQuizWithGuidance((currentQuiz as LessonQuizDto).lessonId, lessonSubmission);
          break;
        case 'level':
          const levelSubmission: LevelQuizSubmissionDto = {
            levelQuizId: currentQuiz.id,
            answers: orderedAnswers,
            ...timePayload
          };
          result = await contentService.submitLevelQuizWithGuidance(levelSubmission);
          break;
        case 'course':
          const courseSubmission: CourseQuizSubmissionDto = {
            courseQuizId: currentQuiz.id,
            answers: orderedAnswers,
            ...timePayload
          };
          result = await contentService.submitCourseQuizWithGuidance(courseSubmission);
          break;
        default:
          throw new Error(`Unsupported quiz type: ${quizType}`);
      }

      setQuizResult(result);
      quizResultRef.current = result;
      notifyGamificationAfterAction({ xpEarned: readXpAwarded(result) });

      // Log quiz submission action
      if (user?.id) {
        try {
          // Ensure logger is initialized
          userActionLogger.initialize();
          await userActionLogger.logUserAction({
            type: 'submit_quiz',
            resourceType: quizType === 'lesson' ? 'lesson_quiz' : quizType === 'level' ? 'level_quiz' : 'course_quiz',
            resourceId: quizId.toString(),
            userId: user.id,
            sessionId: userActionLogger['getSessionId']?.() || 'unknown',
            ipAddress: await userActionLogger['getClientIP']?.() || 'unknown',
            userAgent: navigator.userAgent,
            details: {
              quizId,
              quizType,
              score: result.score,
              passingScore: result.passingScore,
              passed: result.passed,
              totalQuestions: result.totalQuestions,
              correctAnswers: result.correctAnswers,
              timestamp: new Date().toISOString()
            }
          }, {
            component: 'QuizModal',
            feature: 'learning',
            metadata: {
              url: window.location.href
            }
          });
        } catch (logError) {
          console.warn('QuizModal: Failed to log quiz submission:', logError);
        }
      }
    } catch (error: any) {
      // Extract error message more robustly
      let errorMessage = tc('quiz.submitFailed');
      if (error) {
        if (error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.details) {
          errorMessage = error.details;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      }
      const fieldErrors = error?.errors as Record<string, string[] | undefined> | undefined;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const parts = Object.entries(fieldErrors).flatMap(([key, msgs]) =>
          Array.isArray(msgs) && msgs.length ? msgs.map((m) => `${key}: ${m}`) : []
        );
        if (parts.length) errorMessage = `${errorMessage} (${parts.join('; ')})`;
      }
      console.error('Quiz submission error:', error);
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  }, [currentQuiz, quizType, quizId, answers, user, showError, sessionQuestions, t]);

  useEffect(() => {
    if (quizStarted && timeRemaining !== null && timeRemaining > 0 && !submitting && !quizResult) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (quizStarted && timeRemaining === 0 && !submitting && !quizResult) {
      handleSubmit({ autoSubmitOnTimeout: true });
      return undefined;
    }
    return undefined;
  }, [quizStarted, timeRemaining, submitting, quizResult, handleSubmit]);

  /** Current urgency level driven by elapsed time, used for timer color + hints. */
  const timerLevel: 'normal' | 'warning' | 'critical' = useMemo(() => {
    if (
      !quizStarted ||
      quizResult ||
      timeRemaining === null ||
      totalQuizSeconds === null ||
      totalQuizSeconds <= 0
    ) {
      return 'normal';
    }
    const elapsedRatio = (totalQuizSeconds - timeRemaining) / totalQuizSeconds;
    if (elapsedRatio >= 0.9) return 'critical';
    if (elapsedRatio >= 0.7) return 'warning';
    return 'normal';
  }, [quizStarted, quizResult, timeRemaining, totalQuizSeconds]);

  const quizTimeLeftPercent = useMemo(() => {
    if (totalQuizSeconds == null || timeRemaining == null) return null;
    return Math.max(0, Math.round((timeRemaining / totalQuizSeconds) * 100));
  }, [totalQuizSeconds, timeRemaining]);

  // Show a brief 5-second flashing badge the first time we cross each threshold.
  useEffect(() => {
    if (!quizStarted || quizResult || submitting) return;
    if (timeRemaining === null || totalQuizSeconds === null) return;
    if (timerLevel === 'critical' && !criticalHintShownRef.current) {
      criticalHintShownRef.current = true;
      warningHintShownRef.current = true;
      setTimerHint('critical');
      if (timerHintClearTimeoutRef.current != null) {
        window.clearTimeout(timerHintClearTimeoutRef.current);
      }
      timerHintClearTimeoutRef.current = window.setTimeout(() => {
        setTimerHint(null);
        timerHintClearTimeoutRef.current = null;
      }, 5000);
    } else if (timerLevel === 'warning' && !warningHintShownRef.current) {
      warningHintShownRef.current = true;
      setTimerHint('warning');
      if (timerHintClearTimeoutRef.current != null) {
        window.clearTimeout(timerHintClearTimeoutRef.current);
      }
      timerHintClearTimeoutRef.current = window.setTimeout(() => {
        setTimerHint(null);
        timerHintClearTimeoutRef.current = null;
      }, 5000);
    }
  }, [timerLevel, quizStarted, quizResult, submitting, timeRemaining, totalQuizSeconds]);

  useEffect(() => {
    return () => {
      if (timerHintClearTimeoutRef.current != null) {
        window.clearTimeout(timerHintClearTimeoutRef.current);
        timerHintClearTimeoutRef.current = null;
      }
    };
  }, []);

  // Check if Trophy animation file exists and add timeout fallback
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    // First check if file exists
    fetch('/animations/Trophy.lottie', { method: 'HEAD' })
      .then(response => {
        if (!response.ok) {
          setTrophyAnimationError(true);
        } else {
          // If file exists, set a timeout to fallback if animation doesn't load
          timeoutId = setTimeout(() => {
            // Check if canvas inside the container is empty (animation didn't load)
            if (trophyAnimationContainerRef.current) {
              const canvas = trophyAnimationContainerRef.current.querySelector('canvas');
              if (canvas) {
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                if (ctx && canvas.width > 0 && canvas.height > 0) {
                  try {
                    const imageData = ctx.getImageData(0, 0, Math.min(canvas.width, 100), Math.min(canvas.height, 100));
                    // Check if canvas is mostly empty (all pixels are transparent or same color)
                    const data = imageData.data;
                    let hasContent = false;
                    for (let i = 0; i < data.length; i += 4) {
                      const alpha = data[i + 3];
                      if (alpha > 0) {
                        hasContent = true;
                        break;
                      }
                    }
                    if (!hasContent) {
                      console.warn('Trophy animation canvas appears empty, falling back to icon');
                      setTrophyAnimationError(true);
                    }
                  } catch (e) {
                    // If we can't read the canvas (CORS or other issue), assume it failed
                    console.warn('Cannot read trophy animation canvas, falling back to icon');
                    setTrophyAnimationError(true);
                  }
                } else if (canvas.width === 0 || canvas.height === 0) {
                  // Canvas has no dimensions, animation likely didn't load
                  console.warn('Trophy animation canvas has no dimensions, falling back to icon');
                  setTrophyAnimationError(true);
                }
              } else {
                  // No canvas found, animation didn't render
                  console.warn('No trophy animation canvas found, falling back to icon');
                  setTrophyAnimationError(true);
                }
            }
          }, 3000); // Wait 3 seconds for animation to load
        }
      })
      .catch(() => {
        setTrophyAnimationError(true);
      });
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Check if Error animation file exists and add timeout fallback
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    // First check if file exists
    fetch('/animations/Error_Occurred.lottie', { method: 'HEAD' })
      .then(response => {
        if (!response.ok) {
          setErrorAnimationError(true);
        } else {
          // If file exists, set a timeout to fallback if animation doesn't load
          timeoutId = setTimeout(() => {
            // Check if canvas inside the container is empty (animation didn't load)
            if (errorAnimationContainerRef.current) {
              const canvas = errorAnimationContainerRef.current.querySelector('canvas');
              if (canvas) {
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                if (ctx && canvas.width > 0 && canvas.height > 0) {
                  try {
                    const imageData = ctx.getImageData(0, 0, Math.min(canvas.width, 100), Math.min(canvas.height, 100));
                    // Check if canvas is mostly empty (all pixels are transparent or same color)
                    const data = imageData.data;
                    let hasContent = false;
                    for (let i = 0; i < data.length; i += 4) {
                      const alpha = data[i + 3];
                      if (alpha > 0) {
                        hasContent = true;
                        break;
                      }
                    }
                    if (!hasContent) {
                      console.warn('Error animation canvas appears empty, falling back to icon');
                      setErrorAnimationError(true);
                    }
                  } catch (e) {
                    // If we can't read the canvas (CORS or other issue), assume it failed
                    console.warn('Cannot read error animation canvas, falling back to icon');
                    setErrorAnimationError(true);
                  }
                } else if (canvas.width === 0 || canvas.height === 0) {
                  // Canvas has no dimensions, animation likely didn't load
                  console.warn('Error animation canvas has no dimensions, falling back to icon');
                  setErrorAnimationError(true);
                }
              } else {
                  // No canvas found, animation didn't render
                  console.warn('No error animation canvas found, falling back to icon');
                  setErrorAnimationError(true);
                }
            }
          }, 3000); // Wait 3 seconds for animation to load
        }
      })
      .catch(() => {
        setErrorAnimationError(true);
      });
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const normalizeImageUrl = normalizeAppHostedUploadUrl;

  const loadQuiz = async () => {
    try {
      setLoading(true);
      setSessionQuestions(null);
      setSessionMcOptionsByQuestionId({});
      let quiz: UnifiedQuizDto;
      let quizPreviousScore: number | undefined;

      switch (quizType) {
        case 'lesson':
          quiz = await contentService.getLessonQuizByIdForLearner(quizId);
          break;
        case 'level':
          quiz = await contentService.getLevelQuizByIdForLearner(quizId);
          quizPreviousScore = (quiz as LevelQuizDto).previousScore;
          break;
        case 'course':
          quiz = await contentService.getCourseQuizByIdForLearner(quizId);
          quizPreviousScore = (quiz as CourseQuizDto).previousScore;
          break;
        default:
          throw new Error(`Unsupported quiz type: ${quizType}`);
      }

      setCurrentQuiz({
        ...quiz,
        isDesignedForNonFarsiUsers: isDesignedForNonFarsiUsers(quiz),
      });
      setLoadedPreviousScore(
        previousScore !== undefined ? previousScore : quizPreviousScore
      );
      setTimeRemaining(null);
      setCurrentQuestionIndex(0);
      setCurrentPageIndex(0);
      setAnswers({});
      setPuzzleOrders({});
      setQuizResult(null);
      placementFailHandledRef.current = false;
      const shuffleQ = Boolean(
        (quiz as any).shuffleQuestionsOnEachLoad ?? (quiz as any).ShuffleQuestionsOnEachLoad
      );
      const startImmediately = placementDirectStart && !browseMode;
      if (startImmediately) {
        const ordered = orderQuestionsForSession(quiz.questions as any[], shuffleQ);
        setSessionQuestions(ordered);
        setSessionMcOptionsByQuestionId(buildMcOptionShuffleMap(ordered));
        quizSessionStartMsRef.current = Date.now();
        const initialSeconds =
          disableTimeLimit || quiz.timeLimitMinutes === 0 ? null : quiz.timeLimitMinutes * 60;
        setTimeRemaining(initialSeconds);
        setTotalQuizSeconds(initialSeconds);
        warningHintShownRef.current = false;
        criticalHintShownRef.current = false;
        setTimerHint(null);
      }
      setQuizStarted(startImmediately);
    } catch (error) {
      const message = error instanceof Error ? error.message : tc('quiz.loadFailed');
      showError(message);
      handleDismiss();
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = async () => {
    if (currentQuiz) {
      const shuffleQ = Boolean(
        (currentQuiz as any).shuffleQuestionsOnEachLoad ?? (currentQuiz as any).ShuffleQuestionsOnEachLoad
      );
      const ordered = orderQuestionsForSession(currentQuiz.questions as any[], shuffleQ);
      setSessionQuestions(ordered);
      setSessionMcOptionsByQuestionId(buildMcOptionShuffleMap(ordered));
      setQuizStarted(true);
      quizSessionStartMsRef.current = Date.now();
      const initialSeconds =
        disableTimeLimit || currentQuiz.timeLimitMinutes === 0
          ? null
          : currentQuiz.timeLimitMinutes * 60;
      setTimeRemaining(initialSeconds);
      setTotalQuizSeconds(initialSeconds);
      warningHintShownRef.current = false;
      criticalHintShownRef.current = false;
      setTimerHint(null);
      
      // Log quiz start action
      if (user?.id) {
        try {
          // Ensure logger is initialized
          userActionLogger.initialize();
          await userActionLogger.logUserAction({
            type: 'start_quiz',
            resourceType: quizType === 'lesson' ? 'lesson_quiz' : quizType === 'level' ? 'level_quiz' : 'course_quiz',
            resourceId: quizId.toString(),
            userId: user.id,
            sessionId: userActionLogger['getSessionId']?.() || 'unknown',
            ipAddress: await userActionLogger['getClientIP']?.() || 'unknown',
            userAgent: navigator.userAgent,
            details: {
              quizId,
              quizType,
              quizTitle: currentQuiz.title,
              quizDescription: currentQuiz.description,
              timeLimitMinutes: currentQuiz.timeLimitMinutes,
              totalQuestions: currentQuiz.questions.length,
              timestamp: new Date().toISOString()
            }
          }, {
            component: 'QuizModal',
            feature: 'learning',
            metadata: {
              url: window.location.href
            }
          });
        } catch (logError) {
          console.warn('QuizModal: Failed to log quiz start:', logError);
        }
      }
    }
  };

  const handleAnswerChange = (questionId: number, answer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  // Split by page separators: only show next/prev when there are page separators
  const { realQuestions, answerableQuestions, pages, hasMultiplePages } = useMemo(() => {
    const questions = activeQuestions;
    const real = questions.filter((q: any) => !isPageSeparatorQuestion(q));
    const answerable = real.filter((q: any) => !isCommentQuestion(q));
    const pageChunks: number[][] = [];
    let chunk: number[] = [];
    questions.forEach((q: any, idx: number) => {
      if (isPageSeparatorQuestion(q)) {
        if (chunk.length) {
          pageChunks.push(chunk);
          chunk = [];
        }
      } else {
        chunk.push(idx);
      }
    });
    if (chunk.length) pageChunks.push(chunk);
    return {
      realQuestions: real,
      answerableQuestions: answerable,
      pages: pageChunks.length ? pageChunks : [real.map((_: any, i: number) => i)],
      hasMultiplePages: pageChunks.length > 1
    };
  }, [activeQuestions]);

  // Questions to show: current page (when multi-page) or all (single page) — includes comments (display-only)
  const questionsToShow = useMemo(() => {
    if (!activeQuestions.length) return [];
    if (hasMultiplePages) {
      const indices = pages[currentPageIndex] ?? [];
      return indices.map((i: number) => activeQuestions[i]);
    }
    return realQuestions;
  }, [activeQuestions, hasMultiplePages, pages, currentPageIndex, realQuestions]);

  const totalQuestions = answerableQuestions.length;
  const answeredCount = answerableQuestions.filter((q: any) => isQuizAnsweredForProgress(q, answers[q.id])).length;
  const allowUnansweredOnSubmit = Boolean(
    (currentQuiz as any)?.allowUnansweredQuestionsOnSubmit ??
      (currentQuiz as any)?.AllowUnansweredQuestionsOnSubmit
  );
  const isTimeExpiryIncompleteFailure = useMemo(() => {
    if (!quizResult) return false;
    return Boolean(
      (quizResult as any).failedDueToTimeExpiryIncomplete ??
        (quizResult as any).FailedDueToTimeExpiryIncomplete
    );
  }, [quizResult]);
  const canSubmit =
    allowUnansweredOnSubmit || totalQuestions === 0 || answeredCount === totalQuestions;

  // Handle puzzle word drag and drop
  const handlePuzzleWordDrag = useCallback((questionId: number, dragIndex: number, dropIndex: number) => {
    setPuzzleOrders(prev => {
      const currentOrder = prev[questionId] || [];
      const newOrder = [...currentOrder];
      const [dragged] = newOrder.splice(dragIndex, 1);
      if (dragged === undefined) return prev;
      newOrder.splice(dropIndex, 0, dragged);

      // Update answer with comma-separated option IDs in order
      const answerString = newOrder.map(id => id.toString()).join(',');
      setAnswers(prevAnswers => ({ ...prevAnswers, [questionId]: answerString }));

      return { ...prev, [questionId]: newOrder };
    });
  }, []);

  // Resolve drop target from coordinates; returns null if over the dragged card (so we keep last valid target)
  const getPuzzleDropTarget = useCallback((clientX: number, clientY: number): { questionId: number; index: number } | null => {
    let el: Element | null = document.elementFromPoint(clientX, clientY);
    while (el && !(el as HTMLElement).dataset?.puzzleOptionId) el = el.parentElement;
    if (!el) return null;
    const qid = (el as HTMLElement).dataset.puzzleQuestionId;
    const idxStr = (el as HTMLElement).dataset.puzzleIndex;
    const optionIdStr = (el as HTMLElement).dataset.puzzleOptionId;
    if (qid == null || idxStr == null) return null;
    const questionId = parseInt(qid, 10);
    const index = parseInt(idxStr, 10);
    const optionId = optionIdStr ? parseInt(optionIdStr, 10) : null;
    if (optionId !== null && optionId === puzzleDraggedIdRef.current) return null;
    return { questionId, index };
  }, []);

  // Document-level pointer handlers for puzzle drag (touch + mouse; HTML5 drag breaks inside transformed modals).
  const puzzleDocPointerMove = useCallback((e: PointerEvent) => {
    if (puzzleDraggedIdRef.current == null) return;
    e.preventDefault();
    setPuzzleTouchGhost(prev =>
      prev ? { ...prev, clientX: e.clientX, clientY: e.clientY } : prev
    );
    const target = getPuzzleDropTarget(e.clientX, e.clientY);
    if (!target || target.questionId !== puzzleTouchQuestionIdRef.current) return;
    puzzleDragOverIndexRef.current = target;
    setPuzzleDragPreviewIndex(prev => (prev?.questionId === target.questionId && prev?.index === target.index ? prev : target));
  }, [getPuzzleDropTarget]);

  const puzzleDocPointerEnd = useCallback((e: PointerEvent) => {
    const cleanup = puzzleDocTouchCleanupRef.current;
    if (cleanup) {
      puzzleDocTouchCleanupRef.current = null;
      cleanup();
    }
    const questionId = puzzleTouchQuestionIdRef.current;
    const order = puzzleTouchOrderRef.current;
    const draggedId = puzzleDraggedIdRef.current;
    let over = puzzleDragOverIndexRef.current;
    if (questionId != null && draggedId != null && order.length && (over?.questionId !== questionId || over?.index == null)) {
      over = getPuzzleDropTarget(e.clientX, e.clientY);
    }
    puzzleDraggedIdRef.current = null;
    puzzleTouchQuestionIdRef.current = null;
    puzzleDragOverIndexRef.current = null;
    setPuzzleDragPreviewIndex(null);
    setPuzzleTouchGhost(null);
    if (questionId != null) setPuzzleDragging(prev => ({ ...prev, [questionId]: null }));
    if (questionId == null || draggedId == null || !order.length || over?.questionId !== questionId || over?.index == null) return;
    const dragIndex = order.indexOf(draggedId);
    if (dragIndex >= 0 && dragIndex !== over.index) handlePuzzleWordDrag(questionId, dragIndex, over.index);
  }, [handlePuzzleWordDrag, getPuzzleDropTarget]);

  // Initialize puzzle / sort-paragraph orders and default answers for interactive types
  useEffect(() => {
    setPuzzleOrders((prev) => {
      let next = prev;
      questionsToShow.forEach((q: any) => {
        const qt = q?.questionType;
        if (
          (qt !== QuestionType.Puzzle && qt !== QuestionType.SortParagraphs) ||
          !q?.questionOptions?.length
        ) {
          return;
        }
        const questionId = q.id;
        if (prev[questionId]) return;
        const initialOrder = initialOrderFromOptions(q.questionOptions);
        next = { ...next, [questionId]: initialOrder };
      });
      return next;
    });

    setAnswers((prev) => {
      let next = prev;
      let changed = false;
      questionsToShow.forEach((q: any) => {
        const questionId = q?.id;
        if (questionId == null || prev[questionId] !== undefined) return;
        const qt = q.questionType;
        if (qt === QuestionType.Puzzle || qt === QuestionType.SortParagraphs) {
          if (!q.questionOptions?.length) return;
          const initialOrder = initialOrderFromOptions(q.questionOptions);
          next = { ...next, [questionId]: initialOrder.map((id) => String(id)).join(',') };
          changed = true;
        } else if (qt === QuestionType.Essay) {
          next = { ...next, [questionId]: '' };
          changed = true;
        } else if (qt === QuestionType.FindTheWords) {
          next = { ...next, [questionId]: '' };
          changed = true;
        } else if (qt === QuestionType.Crossword) {
          next = { ...next, [questionId]: initialCrosswordAnswer(q) };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [questionsToShow]);

  const handleNextPage = () => {
    if (hasMultiplePages && currentPageIndex < pages.length - 1) {
      setCurrentPageIndex(currentPageIndex + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasMultiplePages && currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /** Search/browse: read-only question list until learner continues to the standard intro. */
  const inBrowseReadOnly =
    browseMode && !browsePreviewConsumed && !quizStarted && !quizResult;

  const quizContentPhase: QuizContentPhase = useMemo(() => {
    if (quizResult) return 'results';
    if (quizStarted || skipIntroPhase) return 'questions';
    if (inBrowseReadOnly) return 'browse';
    return 'intro';
  }, [quizResult, quizStarted, skipIntroPhase, inBrowseReadOnly]);

  const {
    displayedPhase: displayedQuizPhase,
    panelOpen: quizPanelOpen,
  } = useLearnerSlidePhase(quizContentPhase);

  const renderPhaseInteractionDisabled = (phase: QuizContentPhase) => phase === 'browse';

  // Flash and scroll to the question(s) matching the global learner-search
  // term while the modal is in browse-mode preview. Targets paged quizzes too:
  // jumps to the page that contains the first match before flashing.
  const searchFlashPlayedRef = useRef(false);
  const searchFlashPageRef = useRef<number | null>(null);

  useEffect(() => {
    if (searchFlashPlayedRef.current) return;
    if (!currentQuiz?.questions?.length) return;
    if (!inBrowseReadOnly) return;
    const term = searchHighlightTerm?.trim();
    if (!term) return;

    const matchesQuestion = (q: any) => {
      if (!q) return false;
      if (htmlContainsTerm(q.textContent ?? '', term)) return true;
      const opts = q.questionOptions ?? [];
      return opts.some((o: any) => htmlContainsTerm(o.textContent ?? '', term));
    };

    searchFlashPlayedRef.current = true;
    onSearchHighlightConsumed?.();

    const matching = (currentQuiz.questions as any[]).filter(matchesQuestion);
    if (matching.length === 0) return;

    if (hasMultiplePages) {
      const firstId = matching[0].id;
      const matchPageIndex = pages.findIndex((idxs: number[]) =>
        idxs.some((i) => currentQuiz.questions[i]?.id === firstId)
      );
      if (matchPageIndex >= 0 && matchPageIndex !== currentPageIndex) {
        setCurrentPageIndex(matchPageIndex);
      }
      searchFlashPageRef.current = matchPageIndex >= 0 ? matchPageIndex : currentPageIndex;
    } else {
      searchFlashPageRef.current = currentPageIndex;
    }

    setPulsingQuestionIds(new Set(matching.map((q) => q.id as number)));

    window.requestAnimationFrame(() => {
      const first = matching[0];
      const el = first ? questionRefsRef.current.get(first.id) : null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    if (highlightTimerRef.current != null) window.clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = window.setTimeout(() => {
      setPulsingQuestionIds(new Set());
      searchFlashPageRef.current = null;
      highlightTimerRef.current = null;
    }, SEARCH_HIGHLIGHT_DURATION_MS);
  }, [currentQuiz, searchHighlightTerm, inBrowseReadOnly, onSearchHighlightConsumed, hasMultiplePages, pages, currentPageIndex]);

  useEffect(() => {
    if (searchFlashPageRef.current == null || pulsingQuestionIds.size === 0) return;
    if (currentPageIndex === searchFlashPageRef.current) return;
    if (highlightTimerRef.current != null) {
      window.clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    searchFlashPageRef.current = null;
    setPulsingQuestionIds(new Set());
  }, [currentPageIndex, pulsingQuestionIds]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current != null) {
        window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };
  }, []);

  const proceedFromBrowsePreviewToIntro = () => {
    setBrowsePreviewConsumed(true);
  };

  const renderQuizPhaseScrollContent = (phase: QuizContentPhase) => {
    const phaseInteractionDisabled = renderPhaseInteractionDisabled(phase);

    if (phase === 'results' && quizResult) {
      return (
        <QuizResultView
          quizResult={quizResult}
          tc={tc}
          contentForceLtr={false}
          passed={isQuizResultPassed(quizResult)}
          isTimeExpiryIncompleteFailure={isTimeExpiryIncompleteFailure}
          isPlacementFlow={isPlacementFlow}
          localizedGuidanceMessage={localizedGuidanceMessage}
          questionResultsInAttemptOrder={questionResultsInAttemptOrder}
          activeQuestions={activeQuestions}
          trophyAnimationError={trophyAnimationError}
          errorAnimationError={errorAnimationError}
          trophyAnimationContainerRef={trophyAnimationContainerRef}
          errorAnimationContainerRef={errorAnimationContainerRef}
          onRetake={() => {
            setQuizResult(null);
            setAnswers({});
            setCurrentQuestionIndex(0);
            setTimeRemaining(null);
            setTotalQuizSeconds(null);
            setTimerHint(null);
            warningHintShownRef.current = false;
            criticalHintShownRef.current = false;
            setSessionQuestions(null);
            setSessionMcOptionsByQuestionId({});
            setQuizStarted(false);
          }}
          onContinue={async () => {
            if (onFlowStageComplete && quizResult) {
              onFlowStageComplete(quizResult);
              return;
            }
            if (quizResult) {
              notifyParentIfPassed(quizResult);
            }
            handleDismiss();
          }}
          onPlacementNext={() => {
            if (quizResult) onFlowStageComplete?.(quizResult);
          }}
          onPlacementConsiderCurrent={() => onPlacementConsiderCurrent?.()}
        />
      );
    }

    if (phase === 'intro') {
      return (
        <div className="text-center py-8">
          <div className="mb-6">
            <ClipboardList className="w-16 h-16 text-purple-600 dark:text-purple-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{tc('quiz.readyToStart')}</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {disableTimeLimit || currentQuiz!.timeLimitMinutes === 0
                ? tc('quiz.introNoTimeLimit', { count: totalQuestions })
                : tc('quiz.introWithTimeLimit', { count: totalQuestions, minutes: currentQuiz!.timeLimitMinutes })}
            </p>
            {onPlacementSkipEasy && (
              <button
                type="button"
                onClick={onPlacementSkipEasy}
                className="mb-4 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {tc('quiz.tooEasyGoNext')}
              </button>
            )}
            {(() => {
              const displayScore = previousScore ?? loadedPreviousScore ??
                (quizType === 'level' ? (currentQuiz as LevelQuizDto)?.previousScore :
                 quizType === 'course' ? (currentQuiz as CourseQuizDto)?.previousScore : undefined);

              return displayScore !== undefined && displayScore !== null ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-4 max-w-md mx-auto">
                  <div className="flex items-center gap-2 text-yellow-900 dark:text-yellow-200">
                    <Trophy className="w-5 h-5" />
                    <span className="font-semibold">{tc('quiz.previousScore', { score: displayScore })}</span>
                  </div>
                </div>
              ) : null;
            })()}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6 max-w-md mx-auto">
              <div className="flex items-center gap-2 text-blue-900 dark:text-blue-200 mb-2">
                <Clock className="w-5 h-5" />
                <span className="font-semibold">
                  {tc('quiz.timeLimitLabel', {
                    limit:
                      disableTimeLimit || currentQuiz!.timeLimitMinutes === 0
                        ? tc('quiz.noTimeLimitation')
                        : currentQuiz!.timeLimitMinutes === 1
                          ? tc('quiz.minuteCount', { count: 1 })
                          : tc('quiz.minutesCount', { count: currentQuiz!.timeLimitMinutes }),
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2 text-blue-900 dark:text-blue-200 mb-2">
                <ClipboardList className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <span className="font-semibold">{tc('quiz.questionsLabel', { count: totalQuestions })}</span>
              </div>
              <div className="flex items-center gap-2 text-blue-900 dark:text-blue-200">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold">{tc('quiz.passingScoreIntro', { score: effectivePassingScore })}</span>
              </div>
              {!allowUnansweredOnSubmit && (
                <p className="text-sm text-blue-800/90 dark:text-blue-300/90 mt-3 pt-3 border-t border-blue-200/80 dark:border-blue-700/80">
                  {tc('quiz.mustAnswerAll')}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-lg bg-gray-200 px-4 py-2.5 font-medium text-gray-700 hover:bg-gray-300 sm:px-6 sm:py-3"
            >
              {tc('common:cancel')}
            </button>
            <button
              type="button"
              onClick={handleStartQuiz}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 font-medium text-white hover:bg-blue-700 sm:px-6 sm:py-3"
            >
              <Play className="w-5 h-5" />
              {tc('quiz.start')}
            </button>
          </div>
        </div>
      );
    }

    if ((phase === 'browse' || phase === 'questions') && questionsToShow.length > 0) {
      return (
        <div className="flex flex-col gap-4">
          <div
            className={`flex flex-col gap-4 ${
              phaseInteractionDisabled ? 'pointer-events-none select-none' : ''
            }`}
          >
          {questionsToShow.map((currentQuestion: any, questionIndex: number) => {
            const isComment = isCommentQuestion(currentQuestion);
            const isHighlighted = pulsingQuestionIds.has(currentQuestion.id);
            if (isComment) {
              return (
                <div
                  key={currentQuestion.id}
                  ref={(el) => { questionRefsRef.current.set(currentQuestion.id, el); }}
                  className={`rounded-xl p-3 sm:p-4 bg-amber-50/80 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 shadow-sm ${
                    isHighlighted ? 'search-result-highlight scroll-mt-24' : ''
                  }`}
                >
                  <RichContentBlocksView
                    source={currentQuestion}
                    imageAlt="Comment"
                    contentDirection={formattedTextDir}
                    {...(isHighlighted && searchHighlightTerm ? { searchHighlightTerm } : {})}
                  />
                </div>
              );
            }
            return (
            <div
              key={currentQuestion.id}
              ref={(el) => { questionRefsRef.current.set(currentQuestion.id, el); }}
              dir={formattedTextDir}
              className={`rounded-xl p-3 sm:p-4 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-600 shadow-sm hover:shadow-md transition-shadow ${
                quizEnglishLocked ? 'text-start' : ''
              } ${
                isHighlighted ? 'search-result-highlight scroll-mt-24' : ''
              }`}
            >
              <div className="flex items-start gap-3 mb-2">
                <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-semibold flex items-center justify-center text-sm">
                  {answerableQuestions.findIndex((q: any) => q.id === currentQuestion.id) + 1 || questionIndex + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">
                    <RichContentBlocksView
                      source={currentQuestion}
                      imageAlt="Question"
                      contentDirection={formattedTextDir}
                      {...(isHighlighted && searchHighlightTerm ? { searchHighlightTerm } : {})}
                    />
                    {!currentQuestion.textContent?.trim()
                      && !currentQuestion.imageUrl?.trim()
                      && !currentQuestion.audioUrl?.trim()
                      && !currentQuestion.videoUrl?.trim()
                      && !currentQuestion.textFormatting?.trim()
                      && currentQuestion.question ? (
                      <h3 dir={formattedTextDir} className={`mb-2${quizEnglishLocked ? ' text-start' : ''}`}>
                        {currentQuestion.question}
                      </h3>
                    ) : null}
                  </div>

            {(() => {
              const isPuzzle = (currentQuestion as any).questionType === QuestionType.Puzzle;
              const isSortParagraphs =
                (currentQuestion as any).questionType === QuestionType.SortParagraphs;
              const hasQuestionOptions = currentQuestion.questionOptions && currentQuestion.questionOptions.length > 0;

              if (isPuzzle && hasQuestionOptions) {
                const questionId = currentQuestion.id;
                const currentOrder = puzzleOrders[questionId] ||
                  [...(currentQuestion.questionOptions || [])]
                    .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                    .map((opt: any) => opt.id ?? 0);
                const optionMap = new Map<number, any>(
                  (currentQuestion.questionOptions || []).map((opt: any) => [opt.id || 0, opt])
                );
                const puzzleAlignment = quizEnglishLocked
                  ? 'ltr'
                  : (currentQuestion as any).puzzleAlignment || 'rtl';
                const draggedOptionId = puzzleDragging[questionId] ?? null;
                const touchGhostForQuestion =
                  puzzleTouchGhost && puzzleTouchGhost.questionId === questionId ? puzzleTouchGhost : null;
                const extractWordText = (option: any, fallbackIndex: number): string => {
                  const block = option?.blocks?.find((b: any) => b.type === 'text');
                  if (block?.textContent) {
                    const temp = document.createElement('div');
                    temp.innerHTML = block.textContent;
                    return temp.textContent || temp.innerText || `Word ${fallbackIndex + 1}`;
                  }
                  return option?.textContent || `Word ${fallbackIndex + 1}`;
                };

                return (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      {tcQ('interactiveQuestions.puzzleHint', {
                        defaultValue: 'Drag the words below to arrange them in the correct order.',
                      })}
                    </p>
                    <motion.div
                      className="flex flex-wrap gap-2 sm:gap-1.5 p-2.5 sm:p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 w-fit max-w-full"
                      style={{
                        direction: 'ltr',
                        flexDirection: puzzleAlignment === 'rtl' ? 'row-reverse' : 'row',
                      }}
                      layout
                      transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    >
                      <AnimatePresence initial={false}>
                      {(puzzleDragPreviewIndex?.questionId === questionId && puzzleDragPreviewIndex?.index !== undefined && draggedOptionId != null
                        ? (() => {
                            const base = puzzleOrders[questionId] ?? currentOrder;
                            const dragIdx = base.indexOf(draggedOptionId);
                            if (dragIdx < 0) return base;
                            const reordered = [...base];
                            reordered.splice(dragIdx, 1);
                            reordered.splice(puzzleDragPreviewIndex.index, 0, draggedOptionId);
                            return reordered;
                          })()
                        : currentOrder
                      ).map((optionId, idx) => {
                        const option = optionMap.get(optionId);
                        if (!option) return null;
                        const wordText = extractWordText(option, idx);
                        const isDragging = draggedOptionId === optionId;
                        const isTouchGhosted =
                          touchGhostForQuestion?.optionId === optionId;

                        return (
                          <motion.div
                            key={optionId}
                            layout
                            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                            initial={false}
                            data-puzzle-option-id={optionId}
                            data-puzzle-question-id={questionId}
                            data-puzzle-index={idx}
                            onPointerDown={(e) => {
                              if (e.button !== 0) return;
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              puzzleDraggedIdRef.current = optionId;
                              puzzleTouchQuestionIdRef.current = questionId;
                              puzzleTouchOrderRef.current = puzzleOrders[questionId] ?? currentOrder;
                              setPuzzleDragging(prev => ({ ...prev, [questionId]: optionId }));
                              setPuzzleDragPreviewIndex(null);
                              setPuzzleTouchGhost({
                                questionId,
                                optionId,
                                clientX: e.clientX,
                                clientY: e.clientY,
                                offsetX: e.clientX - rect.left,
                                offsetY: e.clientY - rect.top,
                                width: rect.width,
                                height: rect.height,
                              });
                              const cleanup = () => {
                                document.removeEventListener('pointermove', puzzleDocPointerMove);
                                document.removeEventListener('pointerup', puzzleDocPointerEnd);
                                document.removeEventListener('pointercancel', puzzleDocPointerEnd);
                              };
                              puzzleDocTouchCleanupRef.current = cleanup;
                              document.addEventListener('pointermove', puzzleDocPointerMove);
                              document.addEventListener('pointerup', puzzleDocPointerEnd);
                              document.addEventListener('pointercancel', puzzleDocPointerEnd);
                              e.preventDefault();
                            }}
                            className={`inline-flex items-center px-3 py-1.5 sm:px-2 sm:py-1 rounded-md cursor-move transition-colors touch-none select-none ${
                              isDragging
                                ? 'bg-blue-600 dark:bg-blue-700 text-white border border-blue-700 dark:border-blue-800 shadow-md scale-105 z-50'
                                : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-blue-400 dark:border-blue-500 hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-sm'
                            }`}
                            style={{
                              touchAction: 'none',
                              width: 'fit-content',
                              height: 'fit-content',
                              visibility: isTouchGhosted ? 'hidden' : undefined,
                            }}
                          >
                            <span className="font-medium text-sm leading-tight whitespace-nowrap" dir={formattedTextDir ?? 'auto'}>{wordText}</span>
                          </motion.div>
                        );
                      })}
                      </AnimatePresence>
                    </motion.div>
                    {touchGhostForQuestion && (() => {
                      const ghostOption = optionMap.get(touchGhostForQuestion.optionId);
                      if (!ghostOption) return null;
                      return (
                        <PuzzleTouchDragGhost
                          ghost={touchGhostForQuestion}
                          wordText={extractWordText(ghostOption, 0)}
                          textDir={formattedTextDir ?? 'auto'}
                        />
                      );
                    })()}
                  </div>
                );
              }

              if (isSortParagraphs && hasQuestionOptions) {
                const questionId = currentQuestion.id;
                const currentOrder =
                  puzzleOrders[questionId] ||
                  initialOrderFromOptions(currentQuestion.questionOptions);
                const sortAlignment = quizEnglishLocked
                  ? 'ltr'
                  : ((currentQuestion as any).puzzleAlignment || 'rtl') as 'ltr' | 'rtl';
                return (
                  <SortParagraphsPlayer
                    questionId={questionId}
                    options={currentQuestion.questionOptions || []}
                    order={currentOrder}
                    alignment={sortAlignment}
                    hint={tcQ('interactiveQuestions.sortParagraphsHint', {
                      defaultValue:
                        'Drag the paragraphs or use the arrows to put them in the correct order.',
                    })}
                    onOrderChange={(newOrder) => {
                      setPuzzleOrders((prev) => ({ ...prev, [questionId]: newOrder }));
                      handleAnswerChange(questionId, newOrder.map((id) => String(id)).join(','));
                    }}
                  />
                );
              }

              const isEssay = (currentQuestion as any).questionType === QuestionType.Essay;
              if (isEssay) {
                const essayDir =
                  String((currentQuestion as any).puzzleAlignment ?? 'rtl').toLowerCase() === 'ltr'
                    ? 'ltr'
                    : 'rtl';
                return (
                  <EssayAnswerInput
                    value={answers[currentQuestion.id] || ''}
                    onChange={(v) => handleAnswerChange(currentQuestion.id, v)}
                    minLength={(currentQuestion as any).minAnswerLength}
                    maxLength={(currentQuestion as any).maxAnswerLength}
                    textDirection={essayDir}
                    placeholder={tcQ('quiz.answerPlaceholder')}
                  />
                );
              }

              const isFindTheWords =
                (currentQuestion as any).questionType === QuestionType.FindTheWords;
              if (isFindTheWords) {
                const questionId = currentQuestion.id;
                const { gridLetters, rows, cols } = resolveFindTheWordsGrid(currentQuestion);
                const words = getFindTheWordsWords(currentQuestion.questionOptions);
                const foundIds = parseFoundIds(answers[questionId]);
                const qAny = currentQuestion as any;
                return (
                  <FindTheWordsPlayer
                    gridLetters={gridLetters}
                    rows={rows}
                    cols={cols}
                    words={words}
                    foundIds={foundIds}
                    hint={buildFindTheWordsHint(
                      {
                        allowHorizontal: qAny.allowHorizontal,
                        allowVertical: qAny.allowVertical,
                        allowDiagonal: qAny.allowDiagonal,
                      },
                      tcQ,
                      contentIsRtl
                    )}
                    onFoundIdsChange={(ids) =>
                      handleAnswerChange(questionId, serializeFoundIds(ids))
                    }
                  />
                );
              }

              const isCrossword =
                (currentQuestion as any).questionType === QuestionType.Crossword;
              if (isCrossword) {
                const questionId = currentQuestion.id;
                const rows = Number((currentQuestion as any).gridRows) || 0;
                const cols = Number((currentQuestion as any).gridCols) || 0;
                const entries = getCrosswordEntries(currentQuestion.questionOptions);
                const blank = initialCrosswordAnswer(currentQuestion);
                return (
                  <CrosswordPlayer
                    rows={rows}
                    cols={cols}
                    entries={entries}
                    value={answers[questionId] || blank}
                    hint={tcQ('interactiveQuestions.crosswordHint', {
                      defaultValue: 'Tap a cell and type. Use the clues to fill the crossword.',
                    })}
                    onChange={(v) => handleAnswerChange(questionId, v)}
                  />
                );
              }

              const isFillInTheBlank = (currentQuestion as any).questionType === QuestionType.FillInTheBlank;
              if (isFillInTheBlank) {
                const correctOption = currentQuestion.questionOptions?.find(opt => opt.isCorrect);
                const answerDirection = quizEnglishLocked
                  ? 'ltr'
                  : correctOption
                    ? extractTextDirection(correctOption.textFormatting, correctOption.textContent) ?? 'ltr'
                    : 'ltr';

                return (
                  <FarsiKeyboardInput
                    value={answers[currentQuestion.id] || ''}
                    onChange={(v) => handleAnswerChange(currentQuestion.id, v)}
                    keyboardPosition="fixed-above-anchor"
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder={tcQ('quiz.answerPlaceholder')}
                    dir={answerDirection}
                    style={{ direction: answerDirection, textAlign: answerDirection === 'rtl' ? 'right' : 'left' }}
                  />
                );
              }

              const isShortAnswer = (currentQuestion as any).questionType === QuestionType.ShortAnswer;
              if (isShortAnswer) {
                const correctOption = currentQuestion.questionOptions?.find(opt => opt.isCorrect);
                const answerDirection = quizEnglishLocked
                  ? 'ltr'
                  : correctOption
                    ? extractTextDirection(correctOption.textFormatting, correctOption.textContent) ?? 'ltr'
                    : 'ltr';

                return (
                  <FarsiKeyboardInput
                    multiline
                    rows={4}
                    value={answers[currentQuestion.id] || ''}
                    onChange={(v) => handleAnswerChange(currentQuestion.id, v)}
                    keyboardPosition="fixed-above-anchor"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder={tcQ('quiz.answerPlaceholder')}
                    dir={answerDirection}
                    style={{ direction: answerDirection, textAlign: answerDirection === 'rtl' ? 'right' : 'left' }}
                  />
                );
              }

              if (hasQuestionOptions) {
                const baseSorted = [...(currentQuestion.questionOptions || [])].sort(
                  (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
                );
                const displayOpts =
                  sessionMcOptionsByQuestionId[currentQuestion.id] ?? baseSorted;
                return (
                  <div className="space-y-1.5">
                    {displayOpts.map((option: any, idx: number) => {
                        const optionValue = option.id?.toString() || option.textContent || `option-${idx}`;
                        return (
                          <label
                            key={option.id || idx}
                            dir={formattedTextDir}
                            className={`flex items-start gap-3 py-2 px-3 border-2 rounded-lg cursor-pointer transition-all ${
                              quizEnglishLocked ? 'text-start' : ''
                            } ${
                              answers[currentQuestion.id] === optionValue
                                ? 'border-blue-500 bg-blue-50 text-gray-900 dark:bg-blue-900/50 dark:border-blue-400 dark:text-blue-100'
                                : 'border-gray-200 hover:border-gray-300 text-gray-900 dark:border-gray-600 dark:hover:border-gray-500 dark:text-gray-100'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`question-${currentQuestion.id}`}
                              value={optionValue}
                              checked={answers[currentQuestion.id] === optionValue}
                              onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                              className="mt-1 shrink-0"
                            />
                            <span className="flex-1 min-w-0">
                            <RichContentBlocksView
                              source={option}
                              imageAlt="Option"
                              contentDirection={formattedTextDir}
                              {...(isHighlighted && searchHighlightTerm ? { searchHighlightTerm } : {})}
                            />
                            {!option.textContent?.trim()
                              && !option.imageUrl?.trim()
                              && !option.audioUrl?.trim()
                              && !option.videoUrl?.trim()
                              && !option.textFormatting?.trim() ? (
                              <span>{option.textContent || `Option ${idx + 1}`}</span>
                            ) : null}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                );
              } else if (currentQuestion.options) {
                return (
                  <div className="space-y-2">
                    {parseQuizOptions(currentQuestion.options).map((option: string, idx: number) => (
                      <label
                        key={idx}
                        dir={formattedTextDir}
                        className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          quizEnglishLocked ? 'text-start' : ''
                        } ${
                          answers[currentQuestion.id] === option
                            ? 'border-blue-500 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`question-${currentQuestion.id}`}
                          value={option}
                          checked={answers[currentQuestion.id] === option}
                          onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                          className="mt-1 shrink-0"
                        />
                        <span className="flex-1 min-w-0">{option}</span>
                      </label>
                    ))}
                  </div>
                );
              }

              return (
                <input
                  type="text"
                  dir={formattedTextDir}
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm${quizEnglishLocked ? ' text-start' : ''}`}
                  placeholder={tcQ('quiz.answerPlaceholderShort')}
                />
              );
            })()}
                </div>
              </div>
            </div>
          );
          })}
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-center gap-2 border-t border-gray-200 pt-3 dark:border-gray-700 sm:justify-between">
            {hasMultiplePages ? (
              <>
                <button
                  type="button"
                  onClick={handlePrevPage}
                  disabled={currentPageIndex === 0}
                  className={`flex items-center gap-1 rounded-lg px-2.5 py-2 text-sm sm:px-3 ${
                    currentPageIndex === 0
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <ArrowLeft className={navArrowClass('w-4 h-4')} />
                  {tc('quiz.prevPage')}
                </button>
                <span className="text-center text-sm text-gray-600 dark:text-gray-400">
                  {tc('quiz.pageOf', { current: currentPageIndex + 1, total: pages.length })}
                </span>
                {currentPageIndex < pages.length - 1 ? (
                  <button
                    type="button"
                    onClick={handleNextPage}
                    className="flex items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-2 text-sm text-white hover:bg-blue-700 sm:px-3"
                  >
                    {tc('quiz.nextPage')}
                    <ArrowRight className={navArrowClass('w-4 h-4')} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={phaseInteractionDisabled ? proceedFromBrowsePreviewToIntro : () => handleSubmit()}
                    disabled={phaseInteractionDisabled ? false : (!canSubmit || submitting)}
                    className={`px-3 py-2 rounded-lg text-sm ${
                      phaseInteractionDisabled || (canSubmit && !submitting)
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {phaseInteractionDisabled
                      ? tc('quiz.continueEllipsis')
                      : submitting
                        ? tc('quiz.submitting')
                        : tc('quiz.submit')}
                  </button>
                )}
              </>
            ) : (
              <>
                <div />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {phaseInteractionDisabled ? tc('quiz.preview') : tc('quiz.answeredCount', { answered: answeredCount, total: totalQuestions })}
                </span>
                <button
                  type="button"
                  onClick={phaseInteractionDisabled ? proceedFromBrowsePreviewToIntro : () => handleSubmit()}
                  disabled={phaseInteractionDisabled ? false : (!canSubmit || submitting)}
                  className={`px-3 py-2 rounded-lg text-sm ${
                    phaseInteractionDisabled || (canSubmit && !submitting)
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {phaseInteractionDisabled
                    ? tc('quiz.continueEllipsis')
                    : submitting
                      ? tc('quiz.submitting')
                      : tc('quiz.submit')}
                </button>
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No questions available</p>
      </div>
    );
  };

  const slideShellProps = {
    mounted: slideMounted,
    visible: slideVisible,
    panelRef: slidePanelRef,
    onBackdropClick: handleDismiss,
    onPanelTransitionEnd: handleExitTransitionEnd,
    panelClassName: QUIZ_MODAL_PANEL_CLASS,
    ariaLabelledBy: 'quiz-modal-title',
    align: 'center' as const,
    bareContainer: true,
  };

  const renderQuizConfirmDialogs = () => (
    <>
      <LearnerAlertDialog
        open={showExitConfirm}
        title={tc('quiz.exitConfirmTitle', { defaultValue: 'Leave quiz?' })}
        message={tc('quiz.exitConfirmMessage', {
          defaultValue: 'Your quiz will be interrupted and you will need to start again.',
        })}
        primaryLabel={tc('quiz.exitConfirmStay', { defaultValue: 'Keep taking quiz' })}
        secondaryLabel={tc('quiz.exitConfirmLeave', { defaultValue: 'Leave quiz' })}
        onPrimary={() => setShowExitConfirm(false)}
        onSecondary={() => {
          setShowExitConfirm(false);
          forceQuizClose();
        }}
        direction={direction}
        titleId="quiz-exit-confirm-title"
        messageId="quiz-exit-confirm-message"
      />
      <LearnerAlertDialog
        open={partialSubmitCount != null}
        title={tc('quiz.partialSubmitConfirmTitle', { defaultValue: 'Submit with unanswered questions?' })}
        message={tc('quiz.partialSubmitConfirmMessage', {
          count: partialSubmitCount ?? 0,
          defaultValue: '{{count}} question(s) are not fully answered. Submit anyway?',
        })}
        primaryLabel={tc('quiz.partialSubmitConfirmStay', { defaultValue: 'Keep answering' })}
        secondaryLabel={tc('quiz.partialSubmitConfirmSubmit', { defaultValue: 'Submit anyway' })}
        onPrimary={() => setPartialSubmitCount(null)}
        onSecondary={() => {
          setPartialSubmitCount(null);
          void handleSubmit({ skipPartialConfirm: true });
        }}
        direction={direction}
        titleId="quiz-partial-submit-confirm-title"
        messageId="quiz-partial-submit-confirm-message"
      />
    </>
  );

  const portalQuizShell = (shell: React.ReactNode) =>
    portalQuizModal(
      <>
        {shell}
        {renderQuizConfirmDialogs()}
      </>
    );

  const renderQuizPanelBody = (
    phase: QuizContentPhase,
    panelMode: 'content' | 'loading' | 'notFound',
    stackRole: 'solo' | 'outgoing' | 'incoming',
    attachContentScrollRef?: boolean
  ) => {
    if (panelMode === 'loading') {
      return (
        <div
          dir={direction}
          className="flex min-h-[min(40vh,280px)] flex-col items-center justify-center gap-3 p-8"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" aria-hidden />
          <p className="text-sm text-gray-600 dark:text-gray-400">{tc('quiz.loading')}</p>
        </div>
      );
    }
    if (panelMode === 'notFound') {
      return (
        <div className="flex flex-col items-center justify-center p-6 text-center" dir={direction}>
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{tc('quiz.notFound')}</p>
          <button
            type="button"
            onClick={handleDismiss}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('common:close')}
          </button>
        </div>
      );
    }
    if (!currentQuiz) return null;

    const isTallPhase = phase === 'questions' || phase === 'browse' || phase === 'results';

    return (
      <div className="flex max-h-[min(90vh,820px)] flex-col overflow-hidden" dir={direction}>
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-3 py-3 dark:border-gray-700 sm:px-4">
            <div className="min-w-0 flex-1">
              <h2
                {...(stackRole === 'outgoing' ? {} : { id: 'quiz-modal-title' })}
                className="text-lg font-bold text-gray-900 dark:text-gray-100"
              >
                {!quizEnglishLocked && locale === 'fa' ? (
                  <PersianText>{pickTitle(currentQuiz)}</PersianText>
                ) : (
                  pickTitle(currentQuiz)
                )}
              </h2>
              {pickDescription(currentQuiz) && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{pickDescription(currentQuiz)}</p>
              )}
            </div>
            <div className="flex flex-col items-center gap-0.5 shrink-0">
              <button
                onClick={handleDismiss}
                className="self-start p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300"
                aria-label={t('common:close')}
              >
                <X className="w-5 h-5" />
              </button>
              {onEnglishLockedHintClick ? (
                <EnglishLockedContentHintIcon onClick={onEnglishLockedHintClick} />
              ) : null}
            </div>
          </div>

          {phase === 'browse' && inBrowseReadOnly && (
            <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-slate-800/90 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-700 dark:text-gray-300 min-w-0 flex-1">
                {tc('quiz.browsePreviewHint')}
              </p>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  {tc('common:close')}
                </button>
                <button
                  type="button"
                  onClick={proceedFromBrowsePreviewToIntro}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5"
                >
                  <Play className="w-4 h-4" />
                  {tc('quiz.startQuiz')}
                </button>
              </div>
            </div>
          )}

          {phase === 'questions' && (
            <>
              <div
                className={`border-b border-gray-200 px-3 py-2 transition-colors duration-300 dark:border-gray-700 sm:px-4 ${
                  timerLevel === 'critical'
                    ? 'bg-red-50 dark:bg-red-900/30'
                    : timerLevel === 'warning'
                      ? 'bg-orange-50 dark:bg-orange-900/30'
                      : 'bg-blue-50 dark:bg-blue-900/30'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <Clock
                      className={`w-4 h-4 transition-colors duration-300 ${
                        timerLevel === 'critical'
                          ? 'text-red-600 dark:text-red-400'
                          : timerLevel === 'warning'
                            ? 'text-orange-600 dark:text-orange-400'
                            : 'text-blue-600 dark:text-blue-400'
                      }`}
                    />
                    <div className="flex flex-col">
                      <span
                        className={`font-semibold text-sm transition-colors duration-300 tabular-nums ${
                          timerLevel === 'critical'
                            ? 'text-red-600 dark:text-red-400'
                            : timerLevel === 'warning'
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-blue-600 dark:text-blue-400'
                        }`}
                      >
                        {timeRemaining !== null ? formatTime(timeRemaining) : tc('quiz.noLimit')}
                      </span>
                      <span
                        className={`text-xs transition-colors duration-300 ${
                          timerLevel === 'critical'
                            ? 'text-red-700/80 dark:text-red-300/90'
                            : timerLevel === 'warning'
                              ? 'text-orange-700/80 dark:text-orange-300/90'
                              : 'text-blue-700/80 dark:text-blue-300/90'
                        }`}
                      >
                        {tc('quiz.passingScoreLabel', { score: effectivePassingScore })}
                      </span>
                    </div>
                    {timerHint && (() => {
                      const pct = quizTimeLeftPercent ?? (timerHint === 'critical' ? 10 : 30);
                      const hintMessage = tc('quiz.timeLeftHint', { pct });
                      return (
                        <div
                          key={timerHint}
                          role="status"
                          aria-live="polite"
                          aria-label={hintMessage}
                          title={hintMessage}
                          className={`quiz-timer-hint-flash ml-1 inline-flex items-center gap-1 px-2 py-0.5 max-sm:px-1.5 max-sm:py-1 rounded-full text-[11px] max-sm:text-[10px] font-semibold leading-tight shadow-sm shrink-0 sm:whitespace-nowrap ${
                            timerHint === 'critical'
                              ? 'bg-red-100 text-red-700 ring-1 ring-red-300 dark:bg-red-900/60 dark:text-red-200 dark:ring-red-700'
                              : 'bg-orange-100 text-orange-700 ring-1 ring-orange-300 dark:bg-orange-900/60 dark:text-orange-200 dark:ring-orange-700'
                          }`}
                        >
                          <AlertTriangle className="w-3 h-3 shrink-0 self-center hidden sm:block" />
                          <span className="hidden sm:inline">{hintMessage}</span>
                          <span className="flex flex-col items-center text-center leading-none sm:hidden">
                            <span className="inline-flex items-center justify-center gap-0.5">
                              <AlertTriangle className="w-2.5 h-2.5 shrink-0" />
                              {pct}%
                            </span>
                            <span className="text-[9px] font-medium mt-0.5">{tc('quiz.ofTimeLeft')}</span>
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 shrink-0">
                    {hasMultiplePages
                      ? tc('quiz.pageOf', { current: currentPageIndex + 1, total: pages.length })
                      : tc('quiz.answeredCount', { answered: answeredCount, total: totalQuestions })}
                  </div>
                </div>
              </div>
              {placementNavigation && (
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex flex-col items-center gap-1 bg-white dark:bg-gray-900">
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={placementNavigation.onPrev}
                      disabled={!placementNavigation.canPrev}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200"
                    >
                      <ChevronLeft className={navArrowClass('h-4 w-4')} />
                      {tc('quiz.prevQuiz')}
                    </button>
                    <button
                      type="button"
                      onClick={placementNavigation.onNext}
                      disabled={!placementNavigation.canNext}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200"
                    >
                      {tc('quiz.nextQuiz')}
                      <ChevronRight className={navArrowClass('h-4 w-4')} />
                    </button>
                  </div>
                  <p className="text-center text-[11px] text-gray-500 dark:text-gray-400">
                    {tc('quiz.placementNavHint')}
                  </p>
                </div>
              )}
            </>
          )}

          <div
            ref={attachContentScrollRef ? contentScrollRef : undefined}
            className={
              isTallPhase
                ? 'min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-4 sm:px-6 sm:py-6'
                : 'overflow-y-auto overscroll-y-contain px-4 py-4 sm:px-6 sm:py-6'
            }
          >
            {renderQuizPhaseScrollContent(phase)}
          </div>
      </div>
    );
  };

  const quizPanelExtraClass = (phase: QuizContentPhase, panelMode: 'content' | 'loading' | 'notFound') => {
    const isTallPhase =
      (panelMode === 'content' || (panelMode === 'loading' && skipIntroPhase)) &&
      (phase === 'questions' || phase === 'browse' || phase === 'results');
    return isTallPhase ? 'min-h-[min(60vh,520px)]' : '';
  };

  const renderQuizPhasePanel = (
    phase: QuizContentPhase,
    visible: boolean,
    stackRole: 'solo' | 'outgoing' | 'incoming',
    options?: {
      panelRef?: React.RefObject<HTMLDivElement | null>;
      onTransitionEnd?: (e: React.TransitionEvent<HTMLDivElement>) => void;
      attachContentScrollRef?: boolean;
      mode?: 'content' | 'loading' | 'notFound';
      panelKey?: string;
    }
  ) => {
    const panelMode = options?.mode ?? 'content';
    const panelExtraClass = quizPanelExtraClass(phase, panelMode);

    return (
      <LearnerSlideModalPanel
        key={options?.panelKey ?? phase}
        visible={visible}
        stackRole={stackRole}
        panelClassName={`${QUIZ_MODAL_PANEL_CLASS} ${panelExtraClass}`.trim()}
        {...(options?.panelRef ? { panelRef: options.panelRef } : {})}
        {...(options?.onTransitionEnd ? { onTransitionEnd: options.onTransitionEnd } : {})}
        {...(stackRole === 'outgoing' ? {} : { ariaLabelledBy: 'quiz-modal-title' })}
      >
        {renderQuizPanelBody(phase, panelMode, stackRole, options?.attachContentScrollRef)}
      </LearnerSlideModalPanel>
    );
  };

  const activeQuizPanelVisible =
    loading || !currentQuiz ? slideVisible : quizPanelOpen && slideVisible;
  const activePanelPhase: QuizContentPhase =
    loading || !currentQuiz
      ? skipIntroPhase
        ? 'questions'
        : 'intro'
      : displayedQuizPhase;
  const activePanelMode: 'content' | 'loading' | 'notFound' = loading
    ? 'loading'
    : !currentQuiz
      ? 'notFound'
      : 'content';
  if (!slideMounted) {
    return portalQuizModal(renderQuizConfirmDialogs());
  }

  return portalQuizShell(
    <LearnerSlideModalShell {...slideShellProps}>
      {renderQuizPhasePanel(
        activePanelPhase,
        activeQuizPanelVisible,
        'solo',
        {
          panelRef: slidePanelRef,
          onTransitionEnd: handleExitTransitionEnd,
          attachContentScrollRef: activePanelMode === 'content',
          mode: activePanelMode,
          panelKey: 'quiz-active-panel',
        }
      )}
    </LearnerSlideModalShell>
  );
};
